import { Component } from '@angular/core';
import { NotificationService } from '@app/notification-module/services/notification.service';
import { CreatePatronRequest, GetPatronData, PatronService } from '@app/services/patron.service';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

export interface PatronSearchForm {
  searchQuery: string;
}

@Component({
  standalone: false,
  selector: 'app-patron-search-page',
  templateUrl: './patron-search-page.component.html',
  styleUrls: ['./patron-search-page.component.css'],
})
export class PatronSearchPageComponent {
  successMessage = '';
  errorMessage = '';
  isLoading = false;
  searchResults: GetPatronData[] = [];
  private searchSubject = new Subject<string>();

  isAddingToSearchQueue = false;

  showCreateDialog = false;
  isSubmitting = false;
  createForm: CreatePatronForm = {
    firstName: '',
    lastName: '',
    date_of_birth: '',
    ssn: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    ref_id: '',
  };

  constructor(private patronService: PatronService, private notificationService: NotificationService) {
    // Set up real-time search with debouncing
    this.searchSubject
      .pipe(
        debounceTime(500), // Wait 500ms after user stops typing
        distinctUntilChanged(), // Only search if the value has changed
        switchMap((searchTerm) => {
          if (searchTerm && searchTerm.trim().length > 0) {
            this.isLoading = true;
            return this.patronService.searchPatrons(searchTerm.trim()).pipe(
              catchError((error) => {
                console.error('Search error:', error);
                this.isLoading = false;
                this.errorMessage = 'An error occurred while searching. Please try again.';
                return of([]);
              }),
            );
          } else {
            this.isLoading = false;
            this.searchResults = [];
            return of([]);
          }
        }),
        catchError((error) => {
          console.error('Search error:', error);
          this.isLoading = false;
          this.errorMessage = 'An error occurred while searching. Please try again.';
          return of([]);
        }),
      )
      .subscribe((results) => {
        this.isLoading = false;
        if (Array.isArray(results)) {
          this.searchResults = results;
        } else if (results && results.data) {
          this.searchResults = results.data;
        } else {
          this.searchResults = [];
        }
      });
  }

  onSearchInput(event: any): void {
    const searchTerm = event.target.value;
    this.searchSubject.next(searchTerm);
  }

  addToSearchQueue(candidateId: string): void {
    this.isAddingToSearchQueue = true;
    this.patronService.addToSearchQueue(candidateId).subscribe({
      next: () => {
        this.notificationService.success('Patron added to search queue');
        this.isAddingToSearchQueue = false;
      },
      error: (error) => {
        console.error('Error adding to search queue:', error);

        if (error && error.error && error.error.message) {
          this.notificationService.error(error.error.message);
        } else {
          this.notificationService.error('Failed to add to search queue');
        }
        this.isAddingToSearchQueue = false;
      },
    });
  }

  openCreateDialog(): void {
    this.resetCreateForm();
    this.showCreateDialog = true;
  }

  closeCreateDialog(): void {
    if (this.isSubmitting) return;
    this.showCreateDialog = false;
  }

  private resetCreateForm(): void {
    this.createForm = {
      firstName: '',
      lastName: '',
      date_of_birth: '',
      ssn: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      ref_id: '',
    };
  }

  canSubmitCreate(): boolean {
    return (
      !!this.createForm.firstName && !!this.createForm.lastName && !!this.createForm.date_of_birth && !this.isSubmitting
    );
  }

  submitCreatePatron(): void {
    if (!this.canSubmitCreate()) {
      this.notificationService.error('Please fill in required fields');
      return;
    }

    const payload: CreatePatronRequest = {
      name: `${this.createForm.firstName} ${this.createForm.lastName}`.trim(),
      date_of_birth: this.createForm.date_of_birth,
      ssn: this.createForm.ssn,
      address: this.createForm.address,
      city: this.createForm.city,
      state: this.createForm.state,
      postal_code: this.createForm.postal_code,
      ref_id: this.createForm.ref_id,
    };
    this.isSubmitting = true;
    this.patronService.createPatron(payload).subscribe({
      next: () => {
        this.notificationService.success('Patron created successfully');
        this.isSubmitting = false;
        this.showCreateDialog = false;
      },
      error: (error) => {
        console.error('Failed to create patron', error);
        const msg = error && error.error && error.error.message ? error.error.message : 'Failed to create patron';
        this.notificationService.error(msg);
        this.isSubmitting = false;
      },
    });
  }
}

interface CreatePatronForm {
  firstName: string;
  lastName: string;
  date_of_birth: string;
  ssn?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  ref_id?: string;
}
